import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Calendar, ChevronRight, Phone, ArrowRight } from "lucide-react";
import { getBlogPost, blogPosts, company } from "../mock";
import JsonLd from "../components/JsonLd";
import useSEO from "../hooks/useSEO";
import { BIZ_REF, BIZ_ID } from "../seo";
import ContactForm from "../components/ContactForm";

const BlogPost = () => {
  const { slug } = useParams();
  const post = getBlogPost(slug);

  const isoDate = post && new Date(post.date).toISOString().slice(0, 10);
  const seoTitle = post && (`${post.title} | Fab Build Pro`.length <= 60 ? `${post.title} | Fab Build Pro` : post.title);
  useSEO({
    title: seoTitle,
    description: post?.excerpt,
    image: post?.image,
    crumbs: post && [{ name: "Blog", path: "/blog" }, { name: post.title, path: `/blog/${post.slug}` }],
  });

  if (!post) return <Navigate to="/blog" replace />;

  const related = blogPosts.filter((p) => p.slug !== post.slug).slice(0, 2);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `https://fabbuildpro.com/blog/${post.slug}#article`,
    mainEntityOfPage: `https://fabbuildpro.com/blog/${post.slug}`,
    headline: post.title,
    image: post.image,
    datePublished: isoDate,
    dateModified: isoDate,
    articleSection: post.category,
    inLanguage: "en-US",
    author: BIZ_REF,
    about: BIZ_REF,
    publisher: {
      "@type": "Organization",
      "@id": BIZ_ID,
      name: "Fab Build Pro",
      logo: {
        "@type": "ImageObject",
        url: "https://fabbuildpro.com/fab-build-pro-logo.png",
      },
    },
    description: post.excerpt,
  };

  return (
    <>
      <JsonLd data={articleSchema} id="article-schema" />

      <section className="relative bg-brand-navy overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-brand-slate to-brand-navy" />
        <div className="relative max-w-3xl mx-auto px-6 py-16 md:py-20">
          <div className="flex items-center gap-2 text-sm text-white/60 mb-4">
            <Link to="/" className="hover:text-brand-orange transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/blog" className="hover:text-brand-orange transition-colors">Blog</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-brand-orange">{post.category}</span>
          </div>
          <h1 className="font-display font-extrabold text-white text-3xl md:text-4xl leading-tight">
            {post.title}
          </h1>
          <div className="flex items-center gap-3 text-sm text-white/60 mt-4">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" /> {post.date}
            </span>
            <span className="bg-brand-orange/20 text-brand-orange font-semibold px-2.5 py-1 rounded-full">
              {post.category}
            </span>
          </div>
        </div>
      </section>

      <article className="bg-brand-cream py-14">
        <div className="max-w-3xl mx-auto px-6">
          <div className="rounded-3xl overflow-hidden shadow-xl mb-10">
            <img src={post.image} alt={post.title} className="w-full h-[360px] object-cover" />
          </div>
          <div className="space-y-5 text-slate-700 leading-relaxed text-[17px]">
            {post.body?.map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>

          <div className="mt-10 bg-brand-navy rounded-2xl p-7 text-center">
            <p className="text-white font-display font-bold text-xl mb-3">
              Need help with your roof or home project?
            </p>
            <a
              href={company.phoneHref}
              className="inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orangedark text-white font-semibold px-6 py-3.5 rounded-full shadow-lg transition-all hover:-translate-y-0.5"
            >
              <Phone className="w-5 h-5" /> Call {company.phone}
            </a>
          </div>

          {related.length > 0 && (
            <div className="mt-14">
              <h2 className="font-display font-bold text-brand-navy text-2xl mb-6">
                Related Articles
              </h2>
              <div className="grid sm:grid-cols-2 gap-6">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/blog/${r.slug}`}
                    className="group bg-white rounded-2xl overflow-hidden border border-black/5 shadow-sm hover:shadow-lg transition-all"
                  >
                    <div className="h-40 overflow-hidden">
                      <img src={r.image} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-5">
                      <h3 className="font-display font-bold text-brand-navy group-hover:text-brand-orange transition-colors leading-snug">
                        {r.title}
                      </h3>
                      <span className="inline-flex items-center gap-1.5 text-brand-orange font-semibold text-sm mt-3">
                        Read More <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>

      <ContactForm />
    </>
  );
};

export default BlogPost;
